import { useState, useEffect, ReactNode } from "react";
import {
  ColorScheme,
  ColorSchemeContext,
  COLOR_SCHEME_STORAGE_KEY,
} from "@/contexts/ColorSchemeContext";

interface ColorSchemeProviderProps {
  children: ReactNode;
  defaultScheme?: ColorScheme;
}

export function ColorSchemeProvider({
  children,
  defaultScheme = "projection",
}: ColorSchemeProviderProps) {
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() => {
    const stored = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    if (stored === "modern" || stored === "warm" || stored === "projection") {
      return stored;
    }
    return defaultScheme;
  });

  const setColorScheme = (scheme: ColorScheme) => {
    setColorSchemeState(scheme);
    localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, scheme);
  };

  useEffect(() => {
    const root = document.documentElement;

    // The app now ships a single "Neobank" identity defined on the base
    // :root / .dark tokens (see src/index.css and docs/design/redesign-neobank.md).
    // Legacy alternate schemes are retired: ensure no theme-* override class is
    // applied so the base tokens always win. The context API is kept intact so
    // Settings continues to work without change.
    root.classList.remove("theme-modern", "theme-warm", "theme-projection");
  }, [colorScheme]);

  return (
    <ColorSchemeContext.Provider value={{ colorScheme, setColorScheme }}>
      {children}
    </ColorSchemeContext.Provider>
  );
}
